import { confirmJobseekerEdit } from "../lib/jobseekerEdit";
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FileText, Download, X, Upload, Star, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useEscapeKey } from "../lib/useEscapeKey";
import { useCancelRefresh } from "../lib/useRefreshOwner";

/**
 * v3.9.45 — résumé manager popup. Lists all uploaded résumé files, lets the user
 * add up to 5, choose which is active (shown/downloadable on their public profile),
 * and delete. Opened from the profile card's résumé row.
 */
export function ResumeModal({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged?: () => void }) {
  const cancelRefresh = useCancelRefresh();
  const { user } = useAuth();
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  useEscapeKey(onClose, open && !busy);

  async function load() { const d = await api.myResumes().catch(() => null); setRows(d?.attachments || []); }
  useEffect(() => { if (open) { setErr(null); load(); } }, [open]);
  if (!open) return null;

  const count = rows?.length || 0;
  async function upload(file?: File | null) {
    if (!file) return;
    if (count >= 5) { setErr("You can keep at most 5 résumé files. Delete one to add another."); return; }
    cancelRefresh();
    setBusy(true); setErr(null);
    try { await api.uploadResume(file); await load(); onChanged?.(); }
    catch (e: any) { setErr(e?.message || "Could not upload the file."); }
    finally { setBusy(false); }
  }
  async function makeActive(id: string) {
    cancelRefresh();
    setBusy(true); setErr(null);
    try {
      const saved = await confirmJobseekerEdit((confirmed) => api.setActiveResume(id, confirmed));
      if (saved) { await load(); onChanged?.(); }
    } catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not update the résumé."); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    cancelRefresh();
    setBusy(true); setErr(null);
    try {
      const saved = await confirmJobseekerEdit((confirmed) => api.deleteResume(id, confirmed));
      if (saved) { await load(); onChanged?.(); }
    } catch (error: unknown) { setErr(error instanceof Error ? error.message : "Could not update the résumé."); }
    finally { setBusy(false); }
  }
  async function download(id: string) {
    if (!user?.id) { setErr("Could not identify your account."); return; }
    setBusy(true); setErr(null);
    try { await api.downloadResumeAttachment(user.id, id); }
    catch (e: unknown) { setErr(e instanceof Error ? e.message : "Could not download the résumé file."); }
    finally { setBusy(false); }
  }
  const fmtSize = (n?: number) => { const b = Number(n) || 0; return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`; };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label="My résumés" onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div className="card w-full max-w-lg p-0 overflow-hidden max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <FileText className="w-4 h-4 text-navy" />
          <h3 className="font-bold text-navy">My résumés</h3>
          <span className="text-xs text-mut">{count}/5</span>
          <button onClick={onClose} aria-label="Close" className="btn-ghost h-8 w-8 p-0 ml-auto"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="text-xs text-mut">Upload up to 5 files (PDF, Word or image) and pick which one shows on your public profile.</p>
            <input ref={fileInput} type="file" accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
              onChange={(e) => { upload(e.target.files?.[0]); e.currentTarget.value = ""; }} />
            <button id="resume-add-file" onClick={() => fileInput.current?.click()} disabled={busy || count >= 5} className="btn-red h-9 px-3 text-sm shrink-0 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Add
            </button>
          </div>
          {err && <p className="text-sm text-brand-red mb-2">{err}</p>}
          {rows === null ? <p className="text-sm text-mut">Loading…</p>
            : rows.length === 0 ? <p className="text-sm text-mut py-4 text-center">No résumés yet. Upload one to show it on your profile.</p>
            : (
              <div className="space-y-2">
                {rows.map((a) => (
                  <div key={a.id} className={`flex items-center gap-3 rounded-lg border p-3 ${a.active ? "border-brand-green/50 bg-brand-green/5" : "border-line"}`}>
                    <FileText className="w-5 h-5 text-navy shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-navy truncate">{a.fileName}</div>
                      <div className="text-[11px] text-mut">{fmtSize(a.sizeBytes)}{a.active && <span className="text-brand-greendark font-semibold"> · Shown on profile</span>}</div>
                    </div>
                    <button onClick={() => void download(a.id)} disabled={busy} className="btn-ghost h-8 w-8 p-0 disabled:opacity-60" title="View / download"><Download className="w-4 h-4" /></button>
                    {a.active
                      ? <span className="chip bg-brand-green/15 text-brand-greendark flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Active</span>
                      : <button onClick={() => makeActive(a.id)} className="btn-outline h-8 px-2 text-xs flex items-center gap-1"><Star className="w-3 h-3" />Set active</button>}
                    <button onClick={() => remove(a.id)} className="btn-ghost h-8 w-8 p-0 text-brand-red" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
